/* eslint-disable max-classes-per-file */
import * as React from 'react';
import type { VisualTheme } from '@inwink/entities/visualtheme';
import { CMSEditable } from '@inwink/cmstoolbar';
import type { States } from 'services/services';
import { connectwith } from '@inwink/react-utils/decorators';
import type { Entities } from '@inwink/entities/entities';
import { AiToolsContext, IAiToolsContext } from '@inwink/aiinputs/aitoolscontext';
import { openAiTools, AiTextToolsButton } from '@inwink/aiinputs/aitextinput';
import { CMSContextProvider, type ICMSContext } from 'components/cmscontext';
import { WithPopoverManager, IPopoverManager } from '@inwink/modals/popovermgr';
import { i18NHelperContext } from '@inwink/i18n/reactcontext';
import type { IVisualConfigurationStateManager } from '@@components/visualconfig';
import { editJsonModal } from '../jsoneditor';
import type { IAppLinkInputProps } from './applinkinput';
import { getFileName } from './toolbarfilepicker';
import { pickEntities, IEntityPickerModaleProps } from "../entities/entitypicker/entitypickermodale";
import { AppLinkInputModal } from './applinkinput/modal';
import { getTemplates } from '../../api/contenttemplate';
import type { CMSToolbarCommand } from '@inwink/cmstoolbar/commands';
import { getColorPalette } from '@inwink/visualconfiguration';

import './richtextinput.less';


export interface IRichTextInputProps {
    datastamp: any;
    className?: string;
    canEditTrad: boolean;
    multiline?: boolean;
    text: string;
    onChange: (text: string) => void;
    visualConfigurationState?: IVisualConfigurationStateManager;
    showMinRow?: number;
    aiFieldDescription?: string;
    currentLanguage?: string;
    languages?: string[];
    languagesValue?: VisualTheme.IAppLabel;
    onLanguageValueChange?: (val: VisualTheme.IAppLabel) => void;
}
export class RichTextInput extends React.PureComponent<IRichTextInputProps, any> {
    render() {
        return <AiToolsContext.Consumer>
            {(aicontext) => <WithPopoverManager>
                {(popovermgr) => <CMSContextProvider.Consumer>
                    {(cmscontext) => <RichTextInputContent
                        {...this.props}
                        popovermgr={popovermgr}
                        cmscontext={cmscontext}
                        aicontext={aicontext} />}
                </CMSContextProvider.Consumer>}
            </WithPopoverManager>}
        </AiToolsContext.Consumer>;
    }
}

interface IRichTextInputContentProps extends IRichTextInputProps {
    popovermgr: IPopoverManager;
    event?: States.IEventState;
    cmscontext?: ICMSContext;
    aicontext: IAiToolsContext;
}

function mapStateToProps(state: States.IAppState) {
    return {
        event: state.event
    };
}

interface IRichTextInputContentState {
    appendCommands?: CMSToolbarCommand[];
    colorPalette?: string[];
    timestamp: number;
}

@connectwith(mapStateToProps, null)
class RichTextInputContent extends React.PureComponent<IRichTextInputContentProps, IRichTextInputContentState> {
    FileMgr: any;

    LinkMgr: any;

    constructor(props) {
        super(props);
        this.state = {
            colorPalette: [],
            appendCommands: this.getAdditionalCommands(),
            timestamp: Date.now()
            // ds: filesDataSource(this.props.event.eventid)
        };
        this.FileMgr = {
            addFile: function (parent, _props) {
                _props.popovermgr = this.props.popovermgr;
                _props.eventid = this.props.event.eventid;
                // AppFilePickerModal(this.props.popovermgr, parent, _props);
                pickEntities({
                    entityName: "File",
                    multiselect: false,
                    popovermgr: this.props.popovermgr,
                    entityArgs: {
                        ...this.props
                    }
                } as IEntityPickerModaleProps, false, parent).then((files: Entities.IFile[]) => {
                    return this.props.cmscontext.getEntityConfiguration('File', (this.props as any)).then((config) => {
                        return config.datasource.get(files?.length && files[0]?.id, {
                            selects: {
                                $all: true,
                            }
                        }).then((file) => {
                            _props.afterHide();
                            _props.onChange(file);
                        });
                    });
                });
            },
            getFileName: function (fileUrl) {
                return new Promise<any>((resolve) => {
                    getFileName(this.props.event.eventid, fileUrl).then((fileName) => {
                        if (fileName) {
                            resolve(fileName);
                        }
                        resolve("");
                    }, (err) => {
                        console.log(err);
                        resolve("");
                    });
                });
            }
        };
        this.LinkMgr = {
            addLink: function (parent, _props) {
                _props.popovermgr = this.props.popovermgr;
                _props.eventid = this.props.event.eventid;
                return this.props.popovermgr.popoverPortal(parent, AppLinkInputModal, {
                    ..._props,
                    isCmsToolbar: true

                } as IAppLinkInputProps, "linkpicker-modal-content");
            }
        };

        this.FileMgr.addFile = this.FileMgr.addFile.bind(this);
        this.FileMgr.getFileName = this.FileMgr.getFileName.bind(this);
        this.LinkMgr.addLink = this.LinkMgr.addLink.bind(this);
    }

    getAdditionalCommands = () => {
        const commands: CMSToolbarCommand[] = [];
        if (this.props.aicontext && this.props.aiFieldDescription) {
            commands.push({
                builder: () => {
                    return <div className="cmstoolbar-aitools toolbaritem command" onClick={this.showAiTools}>
                        <i className="inwink-sparkles" />
                    </div>;
                }
            });
        }
        if (commands.length > 0) {
            return commands;
        }

        return null;
    };

    showAiTools = (arg) => {
        openAiTools(this.props.popovermgr, this.props.aicontext, arg.currentTarget, {
            languages: this.props.languages,
            multiLanguage: !!this.props.languages,
            targetLanguage: this.props.currentLanguage,
            value: this.props.languagesValue,
            fieldDescription: this.props.aiFieldDescription
        }, () => {
            return this.props.text;
        }, (text) => {
            this.props.onChange(text);

            // Force a re-render after AI tools usage
            setTimeout(() => {
                this.setState(() => ({
                    timestamp: Date.now()
                }));
            }, 500);
        });
    };

    editHTML = (arg: React.MouseEvent<any>) => {
        arg.preventDefault();
        arg.stopPropagation();

        editJsonModal(this.props.popovermgr, this.props.text, null, "html").then((update) => {
            if (update != null) {
                this.props.onChange(update);
            }
        });
    };

    componentDidMount() {
        let application: string = null;
        if (this.props.event?.eventid) {
            application = "event";
        } else if (this.props.cmscontext?.relatedKind === "Community") {
            application = "community";
        }

        if (application) {
            getTemplates(this.props.cmscontext.contentTemplates, application,
                "preview", "config", "visualconfig", null, true)
                .then((contenttemplate: Entities.IContentTemplateBase<any>) => {
                    if (contenttemplate?.config?.global) {
                        this.setState({
                            colorPalette:
                                [
                                    ...this.state.colorPalette,
                                    ...getColorPalette(contenttemplate?.config)
                                ]
                        });
                    }
                });
        }
    }

    render() {
        return <div className={"richtextinput " + (this.props.className || "") + (this.props.multiline ? " multiline" : "")}>
            <i18NHelperContext.Consumer>
                {(i18NHelper) => <CMSContextProvider.Consumer>
                    {(cmscontext) => <CMSEditable
                        key={this.state.timestamp}
                        className="pseudoinput"
                        component="div"
                        appendCommands={this.state.appendCommands}
                        colorPalette={this.state.colorPalette}
                        i18nHelper={i18NHelper}
                        allowRichText={true}
                        text={this.props.text}
                        cmscontext={cmscontext}
                        event={this.props.event}
                        datastamp={this.props.datastamp}
                        ontheflyUpdate={true}
                        fileMgr={this.FileMgr}
                        linkMgr={this.LinkMgr}
                        onChange={this.props.onChange}
                        showMinRow={this.props.showMinRow}
                    />}
                </CMSContextProvider.Consumer>}
            </i18NHelperContext.Consumer>
            <div className="actions">
                <button className="btnEditHTML greybtn" onClick={this.editHTML}><i className="inwink-pencil" /></button>
                <AiTextToolsButton
                    onTextChanged={(text) => {
                        this.props.onChange(text);
                        
                        // Force a re-render after AI tools usage
                        setTimeout(() => {
                            this.setState({
                                timestamp: Date.now()
                            });
                        }, 500);
                    }}
                    popovermgr={this.props.popovermgr}
                    onGetText={() => this.props.text}
                    options={{
                        languages: this.props.languages,
                        multiLanguage: !!this.props.languages,
                        targetLanguage: this.props.currentLanguage,
                        value: this.props.languagesValue,
                        fieldDescription: this.props.aiFieldDescription
                    }}
                />
            </div>
        </div>;
    }
}

export interface IRichi18nTextInputProps {
    className?: string;
    languages: string[];
    canEditTrad: boolean;
    multiline?: boolean;
    datastamp: any;
    text: VisualTheme.IAppLabel;
    onChange: (text: VisualTheme.IAppLabel) => void;
    visualConfigurationState?: IVisualConfigurationStateManager;
    showMinRow?: number;
    aiFieldDescription?: string;
}

// tslint:disable-next-line: max-classes-per-file
export class Richi18nTextInput extends React.PureComponent<IRichi18nTextInputProps, any> {
    render() {
        const showLanguage = this.props.languages.length > 1;
        return <div className={"richi18ntextinput " + (this.props.className || "")}>
            {this.props.languages
                && this.props.languages.map((l) =>
                    <LanguageInput {...this.props} key={l} language={l} showLanguage={showLanguage} />
                )}
        </div>;
    }
}

interface ILanguageInputProps extends IRichi18nTextInputProps {
    language: string;
    showLanguage: boolean;
}
class LanguageInput extends React.PureComponent<ILanguageInputProps, any> {

    cleanHtmlInput(html: string): string {
        // Nettoie les <br> isolés ou suites de <br> ajoutés automatiquement (bug Chrome / éditeur)
        // fait ici plutot que dans cmstoolbar pour gerer aussi celles que l'utilisateur ajoute sans faire attention.
        if (!html) return '';
        const trimmed = html.trim();
        const brOnly = /^((?:<\/?br\s*\/?>(?:\s|&nbsp;)*)+)$/i.test(trimmed) && /<br/i.test(trimmed);
        if (brOnly) return '';
        return html;
    }

    onChange = (txt) => {
        const cleanedTxt = this.cleanHtmlInput(txt);
        let text;
        // Check pour retirer les string non liées à une langue utilisée dans l'event ou non liées à une langue du tout #40501
        if (this.props.languages && this.props.languages.length) {
            this.props.languages.map((language) => {
                const currentLanguageText = this.props.text && this.props.text[language] ? this.props.text[language] : null;
                if (currentLanguageText) {
                    text = Object.assign({}, text, { [language]: currentLanguageText });
                }
            });
        }
        const res = Object.assign({}, text, { [this.props.language]: cleanedTxt });
        this.props.onChange(res);
    };

    render() {
        return <div className="lnginput">
            {this.props.showLanguage ? <div className="languagelabel">{this.props.language}</div> : null}
            <i18NHelperContext.Consumer>
                {() => <RichTextInput
                    className={"lng-" + this.props.language}
                    datastamp={this.props.datastamp}
                    canEditTrad={this.props.canEditTrad}
                    multiline={this.props.multiline}
                    visualConfigurationState={this.props.visualConfigurationState}
                    text={this.props.text && this.props.text[this.props.language]}
                    onChange={this.onChange}
                    showMinRow={this.props.showMinRow}
                    languages={this.props.languages}
                    currentLanguage={this.props.language}
                    languagesValue={this.props.text}
                    onLanguageValueChange={this.props.onChange}
                    aiFieldDescription={this.props.aiFieldDescription}
                />}
            </i18NHelperContext.Consumer>

        </div>;
    }
}
